/**
 * Renders a Guide: where it sits, its title and date, a generated table of
 * contents, its sections, the way into the app, and the other guides.
 *
 * Built from the legal pages' blocks — the same reading width, table of
 * contents and tables — so a guide reads like the rest of the site
 * and a change to how a table or a callout looks happens once. Sections are
 * not numbered: a guide's steps carry their own numbers.
 *
 * The structured data says what the page is in a form a search engine reads
 * exactly: an Article, by BuildPassing, in the page's language, and the trail
 * from the site to the guides to this one.
 */
import { Link } from 'react-router-dom';

import { useI18n } from '../i18n';
import { COMPANY, PRODUCT, SITE_ORIGIN } from '../lib/constants';
import { canonicalFor, ogImageFor, type PagePath } from '../../shared/seo';
import type { Guide } from '../guides/types';
import { BlockView } from './LegalLayout';
import { Button } from './Button';
import { Seo } from './Seo';
import legal from './LegalLayout.module.css';
import styles from './GuideLayout.module.css';

interface GuideLayoutProps {
  guide: Guide;
  /** Path without the locale prefix, e.g. '/guides/budget-by-paycheck'. */
  path: PagePath;
  /** The other guides, in the same language. */
  related: Guide[];
}

export function GuideLayout({ guide, path, related }: GuideLayoutProps) {
  const { t, locale, localePath } = useI18n();

  // UTC, as on the legal pages: '2026-10-19' is midnight UTC, the 18th in Mexico City.
  const updated = new Date(guide.updated).toLocaleDateString(locale === 'es' ? 'es-MX' : 'en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });

  return (
    <>
      <Seo path={path} />
      <GuideData guide={guide} path={path} />

      <div className="container">
        {/*
          Heading, contents, text — in that order in the page, so a phone
          shows the title first and the contents under it. On a wide screen
          the contents move to the left column and stay there as you read.
        */}
        <article className={`${styles.layout} ${styles.guide}`}>
          <header className={styles.head}>
            <nav className={styles.crumbs} aria-label={t.guides.trail}>
              <Link to={localePath('/guides')}>{t.guides.title}</Link>
            </nav>
            <h1 className={legal.title}>{guide.title}</h1>
            <p className={legal.intro}>{guide.intro}</p>
            <div className={legal.dates}>
              <span>
                {t.guides.updated}: <time dateTime={guide.updated}>{updated}</time>
              </span>
            </div>
          </header>

          <nav className={`${legal.toc} ${styles.toc}`} aria-label={t.guides.contents}>
            <h2 className={legal.tocTitle}>{t.guides.contents}</h2>
            <ol className={legal.tocList}>
              {guide.sections.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`} className={legal.tocLink}>
                    {section.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className={styles.body}>
            {guide.sections.map((section) => (
              <section key={section.id} id={section.id} className={legal.section}>
                <h2 className={legal.sectionTitle}>{section.title}</h2>
                {section.blocks.map((block, index) => (
                  <BlockView key={index} block={block} />
                ))}
              </section>
            ))}

            <aside className={styles.cta} aria-labelledby="guide-cta">
              <h2 id="guide-cta" className={styles.ctaTitle}>
                {t.guides.ctaTitle}
              </h2>
              <p className={styles.ctaBody}>{t.guides.ctaBody}</p>
              <Button variant="onNavy" size="lg" href="/app">
                {t.guides.ctaButton}
              </Button>
            </aside>

            <nav className={styles.more} aria-labelledby="more-guides">
              <h2 id="more-guides" className={styles.moreTitle}>
                {t.guides.more}
              </h2>
              <ul className={styles.moreList}>
                {related.map((other) => (
                  <li key={other.id}>
                    <Link to={localePath(`/guides/${other.id}`)} className={styles.moreLink}>
                      <span className={styles.moreName}>{other.title}</span>
                      <span className={styles.moreSummary}>{other.summary}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </article>
      </div>
    </>
  );
}

/** The guide as an Article, and the trail to it. */
function GuideData({ guide, path }: { guide: Guide; path: PagePath }) {
  const { t, locale } = useI18n();
  const url = canonicalFor(path, locale);
  const publisher = {
    '@type': 'Organization',
    name: COMPANY.legalName,
    url: COMPANY.url,
    logo: { '@type': 'ImageObject', url: `${SITE_ORIGIN}/icon-512.png` },
  };

  const data = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: guide.title,
        description: guide.summary,
        inLanguage: locale,
        url,
        mainEntityOfPage: url,
        image: ogImageFor(locale),
        datePublished: guide.published,
        dateModified: guide.updated,
        author: publisher,
        publisher,
        isPartOf: { '@type': 'WebSite', name: PRODUCT.name, url: SITE_ORIGIN },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: PRODUCT.name,
            item: canonicalFor('/', locale),
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: t.guides.title,
            item: canonicalFor('/guides', locale),
          },
          { '@type': 'ListItem', position: 3, name: guide.title, item: url },
        ],
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      // Our own constant text, escaped against a closing tag all the same.
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data).replace(/</g, '\\u003c') }}
    />
  );
}
